import axios from "axios";
import httpClient from "../../clients/httpClient";
import { IProductFilter } from "../../interfaces/product.interface";
import {
    IStore,
    IStoreIntegrationsPayload,
    IStorePayload,
    IStoreResponse,
    IStoreSetupOptions,
    IStoreSetupPayload,
    IStoresPayload,
    UpdateStoreFiltersPayload,
    CopyStoreSettingsPayload,
    IProductSyncStatusResponse,
} from "../../interfaces/store.interface";
import { IStorePrefillResponse } from "../../interfaces/external-store.interface";

export const getStores = ({ ...payload }: IStoresPayload) => {
    const { page, perPage, search, orderBy, sortDirection, rewardsIntegrationId } = payload;
    return httpClient.get('/api/v1/stores', {
        params: {
            page,
            per_page: perPage,
            active_only: 0,
            is_web: 1,
            search,
            load_integrations: 1,
            order_by: orderBy,
            direction: sortDirection,
            rewards_integration_id: rewardsIntegrationId,
        },
    });
};

export const getStoresByTenant = (
    tenantId: number,
    params?: { page?: number; per_page?: number }
) => {
    return httpClient.get<IStoreResponse>(
        `/api/v1/admin/organizations/${tenantId}/stores`,
        { params }
    );
};

export const getStore = (id: string) => {
    return httpClient.get(`/api/v1/stores/${id}`, {
        params: {
            include: 'ecommerceIntegration,sisterStore',
            load_integrations: 1,
            is_web: 1,
            active_only: 0,
        }
    });
};

export const getExternalStores = (integration_id?: number) => {
    return httpClient.get(`/api/v1/external-stores`, {
        params: {
            integration_id
        }
    });
};

export const getStorePrefill = (
    externalStoreId: number | string,
    integrationId?: number
) => {
    return httpClient.get<IStorePrefillResponse>(
        `/api/v1/external-stores/${externalStoreId}/prefill`,
        {
            params: { integration_id: integrationId },
        }
    );
};

export const getExternalInventories = (storeId: number) => {
    return httpClient.get(`/api/v1/stores/${storeId}/external-inventories`);
};

// Raw-axios fetch of the product-sync status, bypassing httpClient. The shared
// httpClient response interceptor toasts and Sentry-captures every failure
// BEFORE the caller's catch runs, so routing this 30s background poll through
// it would spam staff and Sentry on any transient 5xx/network blip — defeating
// the "fail silently, never toast" spec. Mirrors fetchProjectAttributesRaw:
// build the org base URL from the same env vars and attach the Bearer token.
// Returns response.data (the Laravel envelope { data: IProductSyncStatus }) so
// the container's `const { data }` still lands on IProductSyncStatus. Errors
// propagate to the container's silent catch.
export const getStoreProductSyncStatusRequest = async (
    storeId: string
): Promise<IProductSyncStatusResponse> => {
    const org = localStorage.getItem("org");
    const protocol = import.meta.env.VITE_BACKEND_PROTOCOL ?? "https";
    const baseURL = org
        ? `${protocol}://${org}.${import.meta.env.VITE_ORG_API_ENDPOINT}`
        : "";
    const token = JSON.parse(localStorage.getItem("token") || "{}")?.access_token;
    const response = await axios.get<IProductSyncStatusResponse>(
        `${baseURL}/api/v1/stores/${storeId}/product-sync-status`,
        {
            headers: {
                Authorization: token ? `Bearer ${token}` : "",
                Accept: "application/json",
            },
        }
    );
    return response.data;
};

export const createStore = (payload: IStorePayload) => {
    return httpClient.post<IStore>("/api/v1/stores", payload);
};

export const getStoreSetupOptions = () => {
    return httpClient.get<IStoreSetupOptions>("/api/v1/stores/setup/options");
};

export const createStoreSetup = (payload: IStoreSetupPayload) => {
    return httpClient.post<IStore>("/api/v1/stores/setup", payload);
};

export const editStore = (id: number, payload: IStoreIntegrationsPayload) => {
    return httpClient.post<IStore>(`/api/v1/stores/${id}`, payload, {
        params: {
            include: 'sisterStore,ecommerceIntegration',
            load_integrations: 1,
        }
    });
};

export const editStoreIntegrations = (id: number, payload: IStorePayload) => {
    return httpClient.post(`/api/v1/stores/${id}/integrations`, payload, {
        params: {
            include: 'media,sisterStore,ecommerceIntegration,rewardsIntegration,deliveryIntegration,paymentGatewayIntegration,template',
            load_integrations: 1,
        }
    });
};

export const deleteStore = (id: number) => {
    return httpClient.delete(`/api/v1/stores/${id}`);
};

export const getSisterStoreOptions = (medMenu: boolean, recMenu: boolean, storeId?: number) => {
    return httpClient.get(`/api/v1/stores/sister-store-options`, {
        params: {
            med_menu: medMenu,
            rec_menu: recMenu,
            store_id: storeId,
        }
    });
};

export const getStoreProducts = (id: string) => {
    return httpClient.get(`/api/v1/products/?store_id=${id}`);
};


export const getStoreFilteredProducts = (id: string, menuType?: string, filterByRequest?: boolean) => {
    return httpClient.get<IProductFilter>(
        `/api/v1/products/filters`, {
        params: {
            store_id: id,
            show_subcategories: 1,
            filter_by_request: filterByRequest ?? 1,
            menu_type: menuType ?? 'RECREATIONAL'
        }
    }
    );
};

export const updateStoreFilterRequest = (
    id: number,
    payload: UpdateStoreFiltersPayload
) => {
    return httpClient.patch<IStore>(`/api/v1/stores/${id}/filters`, payload);
};


export const cloneStoreSettings = (id: number, payload: CopyStoreSettingsPayload) => {
    return httpClient.post<{ deals_removed: string[], brand_scroller_removed: string[], store: IStore }>(`/api/v1/stores/${id}/clone`, payload);
};

export const syncDeliveryZonesRequest = (id: number) => {
    return httpClient.patch<IStore>(`/api/v1/stores/${id}/sync-delivery-zones`, {}, {
        params: {
            include: 'media,sisterStore,ecommerceIntegration,rewardsIntegration,deliveryIntegration,paymentGatewayIntegration,template',
            load_integrations: 1,
        }
    });
};
